import { inject, Injectable } from '@angular/core'
import type { QueryParams } from '@awork/_shared/services/api-client/ApiClient'
import { ApiClient } from '@awork/_shared/services/api-client/ApiClient'
import { apiEndpoint } from '@awork/environments/environment'
import type { Observable } from 'rxjs'
import { catchError, forkJoin, from, map, of, Subject, switchMap, tap, throwError } from 'rxjs'
import { AccountService } from '@awork/_shared/services/account-service/account.service'
import { WorkspaceQuery } from '@awork/features/workspace/state/workspace.query'
import { Document } from '@awork/features/document/models/document.model'
import { DocumentVersion } from '@awork/features/document/models/document-version.model'
import { DocumentStore } from '@awork/features/document/state/document.store'
import { DocumentSpace } from '@awork/features/document/models/document-space.model'
import { DocumentSpaceStore } from '@awork/features/document/state/document-space.store'
import type { IDocumentSettings } from '@awork/framework/models/view-setting.model'
import { UserSettingsService } from '@awork/_shared/services/user-settings-service/user-settings.service'
import type { DocumentContextEntity, DocumentImportLocation } from '../models/types'
import { DocumentContext } from '../models/types'
import type { Project } from '@awork/features/project/models/project.model'
import type { DocumentContributor } from '../models/document-contributor.model'
import type { DocumentTeamAccess } from '../models/document-team-access.model'
import { SignalService } from '@awork/_shared/services/signal-service/signal.service'
import { DocumentQuery } from '@awork/features/document/state/document.query'
import type { HttpErrorResponse, HttpParams } from '@angular/common/http'
import { SearchService } from '@awork/framework/services/search-service/search.service'
import { UserQuery } from '@awork/features/user/state/user.query'
import { SettingsQuery } from '@awork/framework/state/settings.query'
import { TrackingEvent } from '@awork/_shared/services/tracking-service/events'
import { TrackingService } from '@awork/_shared/services/tracking-service/tracking.service'
import { DocumentSpaceQuery } from '../state/document-space.query'
import type { SyncResponse } from '@awork/core/state/signal-store/types'
import { getLatestSyncDate } from '@awork/core/state/signal-store/helpers'
import { SignalStoreService } from '@awork/core/state/signal-store/signalStore.service'
import { SharedRequest } from '@awork/core/state/signal-store/sharedRequest'
import { MAX_RECENT_DOCUMENTS } from '@awork/framework/models/view-setting.model'
import { guidRegex } from '@awork/_shared/static-data/regular-expressions'

@Injectable({
  providedIn: 'root'
})
export class DocumentService {
  private apiClient = inject(ApiClient)
  private readonly signalStoreService = inject(SignalStoreService)
  private accountService = inject(AccountService)
  private workspaceQuery = inject(WorkspaceQuery)
  private documentStore = inject(DocumentStore)
  private documentQuery = inject(DocumentQuery)
  private documentSpaceStore = inject(DocumentSpaceStore)
  private documentSpaceQuery = inject(DocumentSpaceQuery)
  private userSettingsService = inject(UserSettingsService)
  private signalService = inject(SignalService)
  private searchService = inject(SearchService)
  private userQuery = inject(UserQuery)
  private settingsQuery = inject(SettingsQuery)
  private trackingService = inject(TrackingService)

  currentWorkspace = this.workspaceQuery.queryCurrentWorkspace()

  private documentSettings = this.settingsQuery.query('documents')

  private readonly documentUrl = `${apiEndpoint}/documents`
  private readonly documentSpaceUrl = `${apiEndpoint}/documentspaces`
  private readonly overviewRequest = new SharedRequest<void>()
  private readonly syncDocumentSpacesRequest = new SharedRequest<SyncResponse<DocumentSpace>>()

  documentDeleted = new Subject<string>()
  documentSpaceDeleted = new Subject<string>()

  constructor() {
    this.registerSignal()
  }

  /**
   * Gets the document and spaces signals and triggers the sync on reconnection
   */
  private registerSignal(): void {
    this.signalService.getSignal('document', this.documentStore, this.documentDeleted)
    this.signalService.getSignal('documentspace', this.documentSpaceStore, this.documentSpaceDeleted)

    this.signalService.reconnected.subscribe(() => {
      if (!this.documentSpaceQuery.queryLoading()) {
        this.syncDocumentSpaces().subscribe()
      }
    })
  }

  /**
   * Makes an API call to get the sync information of document spaces and updates the store
   * @returns {Observable<SyncResponse<DocumentSpace>>}
   */
  syncDocumentSpaces(): Observable<SyncResponse<DocumentSpace>> {
    return this.syncDocumentSpacesRequest.share(() => {
      const lastSync = this.documentSpaceQuery.queryLastSync()
      const requiresFullSync = this.documentSpaceQuery.requiresFullSync()
      let request$: Observable<SyncResponse<DocumentSpace>>

      if (requiresFullSync) {
        request$ = this.fetchDocumentSpaces().pipe(
          map(spaces => {
            const lastUpdatedOn = this.documentSpaceQuery.queryLastUpdatedOn()

            return {
              updated: spaces,
              deleted: [],
              lastSync: getLatestSyncDate(lastSync, lastUpdatedOn())
            }
          }),
          tap(syncResponse => {
            this.documentSpaceStore.setLastSync(syncResponse.lastSync)
          })
        )
      } else {
        const issuedAtGeneration = this.documentSpaceStore.getResetGeneration()
        const queryParams = {
          since: lastSync.toISOString()
        }

        request$ = this.apiClient
          .get<SyncResponse<DocumentSpace>>(`${apiEndpoint}/sync/documentspace`, { params: queryParams })
          .pipe(
            tap(response => {
              this.documentSpaceStore.apiSync(response, { issuedAtGeneration })
            })
          )
      }

      return request$
    })
  }

  /**
   * Fetches a document by its id
   * @param {string} documentId
   * @returns {Observable<Document>}
   */
  fetchDocument(documentId: string): Observable<Document> {
    return this.apiClient.get<Document>(`${this.documentUrl}/${documentId}`).pipe(
      map(document => {
        return new Document(document)
      }),
      tap(document => {
        this.documentStore.upsert(document.id, document)
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 404 || error.status === 401) {
          this.documentStore.remove(documentId)
          this.removeDeletedDocumentsFromUserSettings([documentId])
        }

        return throwError(() => error)
      })
    )
  }

  /**
   * Fetches all documents
   * @param {QueryParams} queryParams
   * @returns {Observable<Document[]>}
   */
  fetchDocuments(queryParams?: QueryParams): Observable<Document[]> {
    const params: HttpParams = ApiClient.getQueryParams(queryParams)

    return this.apiClient.getAll<Document[]>(this.documentUrl, { params }).pipe(
      map(documents => {
        return documents.map(document => new Document(document))
      })
    )
  }

  /**
   * Restores persisted stores, then refreshes the accessible overview and space sync state.
   * @returns {Observable<void>}
   */
  syncDocumentOverview(): Observable<void> {
    return this.overviewRequest.share(() =>
      forkJoin([
        this.signalStoreService.selectOnStoreRestored('document'),
        this.signalStoreService.selectOnStoreRestored('documentSpace')
      ]).pipe(
        switchMap(() => {
          const generation = this.documentStore.getResetGeneration()
          const initialDocuments = new Map(
            this.documentQuery
              .getAll()
              .filter(document => !document.movedToTrashOn)
              .map(document => [document.id, document])
          )

          return forkJoin({ documents: this.fetchDocuments(), spaces: this.syncDocumentSpaces() }).pipe(
            tap(({ documents }) => {
              if (generation !== this.documentStore.getResetGeneration()) {
                return
              }

              const currentDocuments = this.documentQuery.getAll()
              const currentById = new Map(currentDocuments.map(document => [document.id, document]))
              // Preserve websocket deletions received while the list request was running.
              const refreshed = documents
                .filter(document => !initialDocuments.has(document.id) || currentById.has(document.id))
                .map(
                  document =>
                    new Document({
                      ...document,
                      content: document.content !== undefined ? document.content : currentById.get(document.id)?.content
                    })
                )
              const returnedIds = new Set(refreshed.map(document => document.id))
              // A list response must not remove documents created or changed after the request started.
              const reconciled = currentDocuments.filter(
                document =>
                  returnedIds.has(document.id) ||
                  (initialDocuments.has(document.id) &&
                    initialDocuments.get(document.id).updatedOn?.getTime() === document.updatedOn?.getTime())
              )

              this.documentStore.sync(refreshed, reconciled)
            }),
            map(() => void 0)
          )
        })
      )
    )
  }

  /**
   * Searches documents using the search service
   * @param {string} query
   * @returns {Observable<Document[]>}
   */
  searchDocuments(query: string): Observable<Document[]> {
    return this.searchService.getSearch(query, 100, ['document']).pipe(
      map(results => {
        const hits = results.documentHits.hits

        return hits.sort((a, b) => b.score - a.score).map(hit => new Document(hit.entity as Document))
      }),
      tap(documents => {
        this.documentStore.upsertMany(documents)
      })
    )
  }

  /**
   * Fetches documents by ids
   * @param {string[]} documentIds
   * @returns {Observable<Document[]>}
   */
  fetchDocumentsByIds(documentIds: string[]): Observable<Document[]> {
    const queryParamsFn = (documentIds: string[]) => ({
      filterBy: documentIds.map(documentId => `id eq guid'${documentId}'`).join(' or ')
    })
    const callFn = (queryParams: QueryParams) => this.fetchDocuments(queryParams)

    return this.apiClient.getBatch(documentIds, queryParamsFn, callFn).pipe(
      map(documents => {
        return documents.map(document => new Document(document))
      }),
      tap(documents => {
        const storedEntities = this.documentQuery.queryDocumentsByIds(documentIds)

        this.documentStore.sync(documents, storedEntities())
      })
    )
  }

  /**
   * Fetches the documents by ids including their descendants
   * @param {string[]} documentIds
   * @returns {Observable<Document[]>}
   */
  fetchDocumentsDescendants(documentIds: string[]): Observable<Document[]> {
    return this.apiClient.post<Document[]>(`${this.documentUrl}/descendants`, { ids: documentIds }).pipe(
      map(documents => {
        return documents.map(document => new Document(document))
      }),
      tap(documents => {
        this.documentStore.upsertMany(documents)
      })
    )
  }

  /**
   * Fetches the favorite documents
   * @returns {Observable<Document[]>}
   */
  fetchFavoriteDocuments(): Observable<Document[]> {
    const favoriteDocumentIds = this.getValidFavoriteDocumentIds()

    if (!favoriteDocumentIds.length) {
      return of([])
    }

    return forkJoin({
      root: this.fetchDocumentsByIds(favoriteDocumentIds),
      descendants: this.fetchDocumentsDescendants(favoriteDocumentIds)
    }).pipe(
      map(documents => {
        return [
          ...documents.root.map(document => new Document(document)),
          ...documents.descendants.map(document => new Document(document))
        ]
      }),
      tap(documents => {
        const storedDocuments = this.documentQuery.queryFavoriteDocuments()
        this.documentStore.sync(documents, storedDocuments())
      })
    )
  }

  /**
   * Returns valid favorite document ids and repairs invalid persisted settings.
   * @returns {string[]} Valid favorite document ids.
   */
  private getValidFavoriteDocumentIds(): string[] {
    const favoriteDocumentIds = this.documentSettings()?.favoriteDocuments ?? []
    const validFavoriteDocumentIds = favoriteDocumentIds.filter(
      documentId => typeof documentId === 'string' && guidRegex.test(documentId)
    )

    if (validFavoriteDocumentIds.length !== favoriteDocumentIds.length) {
      this.saveDocumentUserSettings({ favoriteDocuments: validFavoriteDocumentIds })
    }

    return validFavoriteDocumentIds
  }

  /**
   * Fetches the content of a document
   * @param {string} documentId
   * @returns {Observable<string>}
   */
  fetchDocumentContent(documentId: string): Observable<string> {
    return this.apiClient
      .get<Blob>(`${this.documentUrl}/${documentId}/content`, {
        responseType: 'blob',
        params: { streamAsFile: 'true' }
      })
      .pipe(
        switchMap(blob => {
          return blob ? from(blob.text()) : of('')
        }),
        tap(content => {
          this.documentStore.update(documentId, { content })
        })
      )
  }

  /**
   * Fetches Markdown for copying a document.
   * @param {string} documentId
   * @returns {Observable<string>}
   */
  fetchDocumentMarkdownContent(documentId: string): Observable<string> {
    return this.apiClient
      .get<Blob>(`${this.documentUrl}/${documentId}/content`, {
        responseType: 'blob',
        params: {
          streamAsFile: 'true',
          format: 'markdown'
        }
      })
      .pipe(
        switchMap(blob => {
          return blob ? from(blob.text()) : of('')
        })
      )
  }

  /**
   * Fetches all the document versions of a particular document
   * @param {string} documentId
   * @returns {Observable<DocumentVersion[]>}
   */
  fetchDocumentVersions(documentId: string): Observable<DocumentVersion[]> {
    return this.apiClient.getAll<DocumentVersion[]>(`${this.documentUrl}/${documentId}/versions`).pipe(
      map(documentVersions => {
        return documentVersions.map(version => new DocumentVersion(version))
      })
    )
  }

  /**
   * Fetches the content of a specific document version
   * @param {string} documentId
   * @param {string} versionId
   * @returns {Observable<string>}
   */
  fetchVersionContent(documentId: string, versionId: string): Observable<string> {
    return this.apiClient
      .get<Blob>(`${this.documentUrl}/${documentId}/versions/${versionId}/content`, {
        responseType: 'blob',
        params: { streamAsFile: 'true' }
      })
      .pipe(
        switchMap(blob => {
          return blob ? from(blob.text()) : of('')
        })
      )
  }

  /**
   * Restores a specific document version
   * @param {string} documentId
   * @param {string} versionId
   * @returns {Observable<DocumentVersion>}
   */
  restoreDocumentVersion(documentId: string, versionId: string): Observable<DocumentVersion> {
    const document = this.documentQuery.queryDocument(documentId)
    const operationId = this.documentStore.history.startOperation({
      type: 'update',
      entities: [document()]
    })

    return this.apiClient
      .post<DocumentVersion>(`${this.documentUrl}/${documentId}/versions/${versionId}/restore`, {})
      .pipe(
        map(documentVersion => new DocumentVersion(documentVersion)),
        tap(documentVersion => {
          this.documentStore.update(documentId, {
            currentDocumentVersionId: documentVersion.id,
            updatedOn: documentVersion.updatedOn,
            updatedBy: documentVersion.updatedBy
          })
          this.documentStore.history.endOperation(operationId)
        }),
        catchError(error => {
          this.documentStore.history.undo(operationId)
          return throwError(() => error)
        })
      )
  }

  /**
   * Creates a new document
   * @param {Document} document
   * @returns {Observable<Document>}
   */
  sendDocument(document: Document): Observable<Document> {
    const formData = this.createDocumentFormData(document)

    return this.apiClient.post<Document>(this.documentUrl, formData).pipe(
      map(newDocument => {
        return new Document(newDocument)
      }),
      tap(newDocument => {
        newDocument.content = document.content
        this.documentStore.add(newDocument)
      })
    )
  }

  /**
   * Imports a Markdown file into the selected location. The server converts its content to the editor format.
   * @param {File} file - The Markdown file to import.
   * @param {DocumentImportLocation} location - The location chosen for the new Doc.
   * @returns {Observable<Document>} The created Doc.
   */
  importMarkdownDocument(file: File, location: DocumentImportLocation): Observable<Document> {
    const document = new Document({ ...location, name: file.name.replace(/\.md$/i, '') })
    const formData = this.createDocumentFormData(document)

    formData.append('contentFormat', 'markdown')
    formData.append('content', file, file.name)

    return this.apiClient.post<Document>(this.documentUrl, formData).pipe(
      map(document => new Document(document)),
      tap(document => this.documentStore.add(document))
    )
  }

  /**
   * Converts a Markdown file to Doc content HTML without saving the target Doc.
   * The caller decides whether to append or replace the returned content before saving.
   * @param {string} documentId - The target Doc ID.
   * @param {File} file - The Markdown file to convert.
   * @returns {Observable<string>} The converted Doc content HTML.
   */
  convertDocumentMarkdown(documentId: string, file: File): Observable<string> {
    const formData = new FormData()

    formData.append('content', file, file.name)

    return this.apiClient
      .post<{ id: string; content: string }>(`${this.documentUrl}/${documentId}/content/convertMarkdown`, formData)
      .pipe(map(response => response.content))
  }

  /**
   * Updates a document
   * @param {Document} document
   * @returns {Observable<Document>}
   */
  updateDocument(document: Document): Observable<Document> {
    const operationId = this.documentStore.history.startOperation({ type: 'update', entities: [document] })

    this.documentStore.update(document.id, { ...document, updatedOn: new Date() })

    const putData = { ...document, content: undefined }

    return this.apiClient.put<Document>(`${this.documentUrl}/${document.id}`, putData).pipe(
      map(document => {
        return new Document(document)
      }),
      tap(updatedDocument => {
        this.documentStore.update(document.id, updatedDocument)
        this.documentStore.history.endOperation(operationId)
      }),
      catchError(error => {
        this.documentStore.history.undo(operationId)
        return throwError(error)
      })
    )
  }

  /**
   * Updates the content of a document
   * @param {Document} document
   * @param {Record<string, string>} [queryParams={}]
   * @returns {Observable<Document>}
   */
  updateDocumentContent(document: Document, queryParams: Record<string, string> = {}): Observable<Document> {
    const operationId = this.documentStore.history.startOperation({ type: 'update', entities: [document] })

    this.documentStore.update(document.id, { ...document, updatedOn: new Date() })

    const file = new File([document.content ?? ''], `${document.id}.txt`, { type: 'text/plain' })

    const formData = new FormData()

    formData.append('content', file, file.name)

    return this.apiClient
      .put<Document>(`${this.documentUrl}/${document.id}/content`, formData, { params: queryParams })
      .pipe(
        map(document => {
          return new Document(document)
        }),
        tap(() => {
          this.documentStore.history.endOperation(operationId)
        }),
        catchError(error => {
          this.documentStore.history.undo(operationId)
          return throwError(error)
        })
      )
  }

  /**
   * Updates the document in the store
   * @param {Document} document
   */
  updateDocumentInStore(document: Document): void {
    this.documentStore.update(document.id, { ...document, updatedOn: new Date() })
  }

  /**
   * Updates the location of a document
   * @param {Document} document
   * @param {DocumentContextEntity} documentContextEntity
   * @param {Document} destinationDocument
   * @returns {Observable<Document>}
   */
  updateDocumentLocation(
    document: Document,
    documentContextEntity: DocumentContextEntity,
    destinationDocument?: Document
  ): Observable<Document> {
    const previousProjectId = document.projectId

    document.documentSpaceId = null
    document.projectId = null
    document.isPrivate = false
    document.parentId = null
    // set the order to null in order for the backend to recalculate its position
    // in the new list of documents
    document.order = null

    switch (documentContextEntity.type) {
      case DocumentContext.Space: {
        const documentSpace = documentContextEntity.entity as DocumentSpace

        document.documentSpaceId = documentSpace.id
        document.taskId = null
        break
      }
      case DocumentContext.Project: {
        const project = documentContextEntity.entity as Project

        if (previousProjectId !== project.id) {
          document.taskId = null
        }

        document.projectId = project.id

        break
      }
      case DocumentContext.Private:
        document.isPrivate = true
        document.taskId = null
        break
    }

    if (destinationDocument) {
      document.parentId = destinationDocument?.id
    }

    return this.updateDocument(document)
  }

  /**
   * Deletes a document
   * @param {string} documentId
   * @returns {Observable<void>}
   */
  deleteDocument(documentId: string): Observable<void> {
    return this.apiClient.delete<void>(`${this.documentUrl}/${documentId}`).pipe(
      tap(() => {
        const currentUser = this.userQuery.queryCurrentUser()
        const descendantDocuments = this.documentQuery.queryDescendantDocuments([documentId])
        const descendantDocumentIds = descendantDocuments().map(document => document.id)

        this.documentStore.update([documentId, ...descendantDocumentIds], {
          movedToTrashOn: new Date(),
          movedToTrashBy: currentUser().id
        })

        this.removeDeletedDocumentsFromUserSettings([documentId, ...descendantDocumentIds])
      })
    )
  }

  /**
   * Restores a document
   * @param {Document} document
   * @returns {Observable<void>}
   */
  restoreDocument(document: Document): Observable<void> {
    return this.apiClient.post<void>(`${this.documentUrl}/trash/${document.id}/restore`, {}).pipe(
      tap(() => {
        const descendantDocuments = this.documentQuery.queryDescendantDocuments([document.id])
        const descendantDocumentIds = descendantDocuments().map(document => document.id)

        // Check if the parent document is deleted to restore the document as root
        let isParentDocumentDeleted = false

        if (document.parentId) {
          const parentDocument = this.documentQuery.queryDocument(document.parentId)

          isParentDocumentDeleted = !!parentDocument()?.movedToTrashOn
        }

        this.documentStore.update(document.id, {
          movedToTrashOn: undefined,
          movedToTrashBy: undefined,
          parentId: isParentDocumentDeleted ? undefined : document.parentId
        })

        if (descendantDocumentIds.length) {
          this.documentStore.update(descendantDocumentIds, {
            movedToTrashOn: undefined,
            movedToTrashBy: undefined
          })
        }
      })
    )
  }

  /**
   * Deletes a document permanently
   * @param {string} documentId
   * @returns {Observable<void>}
   */
  deleteDocumentPermanently(documentId: string): Observable<void> {
    return this.apiClient.delete<void>(`${this.documentUrl}/trash/${documentId}`).pipe(
      tap(() => {
        const descendantDocuments = this.documentQuery.queryDescendantDocuments([documentId])
        const descendantDocumentIds = descendantDocuments().map(document => document.id)

        this.documentDeleted.next(documentId)
        this.documentStore.remove([documentId, ...descendantDocumentIds])
      })
    )
  }

  /**
   * Creates the document contibutors
   * @param {string} documentId
   * @param {Pick<DocumentContributor, 'userId' | 'accessLevel'>[]} contributors
   * @returns {Observable<DocumentContributor[]>}
   */
  sendDocumentContributor(
    documentId: string,
    contributors: Pick<DocumentContributor, 'userId' | 'accessLevel'>[]
  ): Observable<DocumentContributor[]> {
    return this.apiClient
      .post<DocumentContributor[]>(`${this.documentUrl}/${documentId}/contributors`, contributors)
      .pipe(
        tap(contributors => {
          this.documentStore.update(documentId, document => {
            return {
              contributors: [...document.contributors, ...contributors]
            }
          })
          this.trackingService.trackEvent(TrackingEvent.docShared)
        })
      )
  }

  /**
   * Updates the document space contibutor
   * @param {string} documentId
   * @param {Pick<DocumentContributor, 'id' | 'userId' | 'accessLevel'>} contributor
   * @returns {Observable<DocumentContributor>}
   */
  updateDocumentContributor(
    documentId: string,
    contributor: Pick<DocumentContributor, 'id' | 'userId' | 'accessLevel'>
  ): Observable<DocumentContributor> {
    return this.apiClient
      .put<DocumentContributor>(`${this.documentUrl}/${documentId}/contributors/${contributor.id}`, contributor)
      .pipe(
        tap(updatedContributor => {
          this.documentStore.update(documentId, document => {
            return {
              contributors: document.contributors.map(contributor => {
                if (contributor.id === updatedContributor.id) {
                  return updatedContributor
                }

                return contributor
              })
            }
          })
        })
      )
  }

  /**
   * Deletes the document space contibutor
   * @param {string} documentId
   * @param {string} contributorId
   * @returns {Observable<void>}
   */
  deleteDocumentContributor(documentId: string, contributorId: string): Observable<void> {
    return this.apiClient.delete<void>(`${this.documentUrl}/${documentId}/contributors/${contributorId}`).pipe(
      tap(() => {
        this.documentStore.update(documentId, document => {
          return {
            contributors: document.contributors.filter(c => c.id !== contributorId)
          }
        })
      })
    )
  }

  /**
   * Creates the document space teams access
   * @param {string} documentId
   * @param {Pick<DocumentTeamAccess, 'teamId' | 'accessLevel'>[]} teamsAccess
   * @returns {Observable<DocumentTeamAccess[]>}
   */
  sendDocumentTeamAccess(
    documentId: string,
    teamsAccess: Pick<DocumentTeamAccess, 'teamId' | 'accessLevel'>[]
  ): Observable<DocumentTeamAccess[]> {
    return this.apiClient.post<DocumentTeamAccess[]>(`${this.documentUrl}/${documentId}/teams`, teamsAccess).pipe(
      tap(teamsAccess => {
        this.documentStore.update(documentId, document => {
          return {
            teams: [...document.teams, ...teamsAccess]
          }
        })
      })
    )
  }

  /**
   * Updates the document space team access
   * @param {string} documentId
   * @param {Pick<DocumentTeamAccess, 'id' | 'teamId' | 'accessLevel'>} teamAccess
   * @returns {Observable<DocumentTeamAccess>}
   */
  updateDocumentTeamAccess(
    documentId: string,
    teamAccess: Pick<DocumentTeamAccess, 'id' | 'teamId' | 'accessLevel'>
  ): Observable<DocumentTeamAccess> {
    return this.apiClient
      .put<DocumentTeamAccess>(`${this.documentUrl}/${documentId}/teams/${teamAccess.id}`, teamAccess)
      .pipe(
        tap(updatedTeamAccess => {
          this.documentStore.update(documentId, document => {
            return {
              teams: document.teams.map(team => {
                if (team.id === updatedTeamAccess.id) {
                  return updatedTeamAccess
                }

                return team
              })
            }
          })
        })
      )
  }

  /**
   * Deletes the document space team access
   * @param {string} documentId
   * @param {string} teamAccessId
   * @returns {Observable<void>}
   */
  deleteDocumentTeamAccess(documentId: string, teamAccessId: string): Observable<void> {
    return this.apiClient.delete<void>(`${this.documentUrl}/${documentId}/teams/${teamAccessId}`).pipe(
      tap(() => {
        this.documentStore.update(documentId, document => {
          return {
            teams: document.teams.filter(c => c.id !== teamAccessId)
          }
        })
      })
    )
  }

  /**
   * Fetches a document space by its id
   * @param {string} documentSpaceId
   * @returns {Observable<DocumentSpace>}
   */
  fetchDocumentSpace(documentSpaceId: string): Observable<DocumentSpace> {
    return this.apiClient.get<DocumentSpace>(`${this.documentSpaceUrl}/${documentSpaceId}`).pipe(
      map(documentSpace => {
        return new DocumentSpace(documentSpace)
      }),
      tap(documentSpace => {
        this.documentSpaceStore.upsert(documentSpace.id, documentSpace)
      })
    )
  }

  /**
   * Fetches all document spaces
   * @returns {Observable<DocumentSpace[]>}
   */
  fetchDocumentSpaces(): Observable<DocumentSpace[]> {
    return this.apiClient.getAll<DocumentSpace[]>(this.documentSpaceUrl).pipe(
      map(documentSpaces => {
        return documentSpaces.map(documentSpace => new DocumentSpace(documentSpace))
      }),
      tap(documentSpaces => {
        this.documentSpaceStore.set(documentSpaces)
      })
    )
  }

  /**
   * Creates a new document space
   * @param {DocumentSpace} documentSpace
   * @returns {Observable<DocumentSpace>}
   */
  sendDocumentSpace(documentSpace: DocumentSpace): Observable<DocumentSpace> {
    return this.apiClient.post<DocumentSpace>(this.documentSpaceUrl, documentSpace).pipe(
      map(newDocumentSpace => {
        return new DocumentSpace(newDocumentSpace)
      }),
      tap(newDocumentSpace => {
        this.documentSpaceStore.add(newDocumentSpace)
      })
    )
  }

  /**
   * Updates a document space
   * @param {DocumentSpace} documentSpace
   * @returns {Observable<DocumentSpace>}
   */
  updateDocumentSpace(documentSpace: DocumentSpace): Observable<DocumentSpace> {
    const operationId = this.documentSpaceStore.history.startOperation({ type: 'update', entities: [documentSpace] })

    this.documentSpaceStore.update(documentSpace.id, documentSpace)

    return this.apiClient.put<DocumentSpace>(`${this.documentSpaceUrl}/${documentSpace.id}`, documentSpace).pipe(
      map(updatedDocumentSpace => {
        return new DocumentSpace(updatedDocumentSpace)
      }),
      tap(() => {
        this.documentSpaceStore.history.endOperation(operationId)
      }),
      catchError(error => {
        this.documentSpaceStore.history.undo(operationId)
        return throwError(error)
      })
    )
  }

  /**
   * Deletes a document space
   * @param {string} documentSpaceId
   * @returns {Observable<void>}
   */
  deleteDocumentSpace(documentSpaceId: string): Observable<void> {
    return this.apiClient.delete<void>(`${this.documentSpaceUrl}/${documentSpaceId}`).pipe(
      tap(() => {
        this.documentSpaceDeleted.next(documentSpaceId)
        this.documentSpaceStore.remove(documentSpaceId)
      })
    )
  }

  /**
   * Creates the document space contibutors
   * @param {string} documentSpaceId
   * @param {Pick<DocumentContributor, 'userId' | 'accessLevel'>[]} contributors
   * @returns {Observable<DocumentContributor[]>}
   */
  sendDocumentSpaceContributor(
    documentSpaceId: string,
    contributors: Pick<DocumentContributor, 'userId' | 'accessLevel'>[]
  ): Observable<DocumentContributor[]> {
    return this.apiClient
      .post<DocumentContributor[]>(`${this.documentSpaceUrl}/${documentSpaceId}/contributors`, contributors)
      .pipe(
        tap(contributors => {
          this.documentSpaceStore.update(documentSpaceId, documentSpace => {
            return {
              contributors: [...documentSpace.contributors, ...contributors]
            }
          })
        })
      )
  }

  /**
   * Updates the document space contibutor
   * @param {string} documentSpaceId
   * @param {Pick<DocumentContributor, 'id' | 'userId' | 'accessLevel'>} contributor
   * @returns {Observable<DocumentContributor>}
   */
  updateDocumentSpaceContributor(
    documentSpaceId: string,
    contributor: Pick<DocumentContributor, 'id' | 'userId' | 'accessLevel'>
  ): Observable<DocumentContributor> {
    return this.apiClient
      .put<DocumentContributor>(
        `${this.documentSpaceUrl}/${documentSpaceId}/contributors/${contributor.id}`,
        contributor
      )
      .pipe(
        tap(updatedContributor => {
          this.documentSpaceStore.update(documentSpaceId, documentSpace => {
            return {
              contributors: documentSpace.contributors.map(contributor => {
                if (contributor.id === updatedContributor.id) {
                  return updatedContributor
                }

                return contributor
              })
            }
          })
        })
      )
  }

  /**
   * Deletes the document space contibutor
   * @param {string} documentSpaceId
   * @param {string} contributorId
   * @returns {Observable<void>}
   */
  deleteDocumentSpaceContributor(documentSpaceId: string, contributorId: string): Observable<void> {
    return this.apiClient
      .delete<void>(`${this.documentSpaceUrl}/${documentSpaceId}/contributors/${contributorId}`)
      .pipe(
        tap(() => {
          this.documentSpaceStore.update(documentSpaceId, documentSpace => {
            return {
              contributors: documentSpace.contributors.filter(c => c.id !== contributorId)
            }
          })
        })
      )
  }

  /**
   * Creates the document space teams access
   * @param {string} documentSpaceId
   * @param {Pick<DocumentTeamAccess, 'teamId' | 'accessLevel'>[]} teamsAccess
   * @returns {Observable<DocumentTeamAccess[]>}
   */
  sendDocumentSpaceTeamAccess(
    documentSpaceId: string,
    teamsAccess: Pick<DocumentTeamAccess, 'teamId' | 'accessLevel'>[]
  ): Observable<DocumentTeamAccess[]> {
    return this.apiClient
      .post<DocumentTeamAccess[]>(`${this.documentSpaceUrl}/${documentSpaceId}/teams`, teamsAccess)
      .pipe(
        tap(teamsAccess => {
          this.documentSpaceStore.update(documentSpaceId, documentSpace => {
            return {
              teams: [...documentSpace.teams, ...teamsAccess]
            }
          })
          this.trackingService.trackEvent(TrackingEvent.docShared)
        })
      )
  }

  /**
   * Updates the document space team access
   * @param {string} documentSpaceId
   * @param {Pick<DocumentTeamAccess, 'id' | 'teamId' | 'accessLevel'>} teamAccess
   * @returns {Observable<DocumentTeamAccess>}
   */
  updateDocumentSpaceTeamAccess(
    documentSpaceId: string,
    teamAccess: Pick<DocumentTeamAccess, 'id' | 'teamId' | 'accessLevel'>
  ): Observable<DocumentTeamAccess> {
    return this.apiClient
      .put<DocumentTeamAccess>(`${this.documentSpaceUrl}/${documentSpaceId}/teams/${teamAccess.id}`, teamAccess)
      .pipe(
        tap(updatedTeamAccess => {
          this.documentSpaceStore.update(documentSpaceId, documentSpace => {
            return {
              teams: documentSpace.teams.map(team => {
                if (team.id === updatedTeamAccess.id) {
                  return updatedTeamAccess
                }

                return team
              })
            }
          })
        })
      )
  }

  /**
   * Deletes the document space team access
   * @param {string} documentSpaceId
   * @param {string} teamAccessId
   * @returns {Observable<void>}
   */
  deleteDocumentSpaceTeamAccess(documentSpaceId: string, teamAccessId: string): Observable<void> {
    return this.apiClient.delete<void>(`${this.documentSpaceUrl}/${documentSpaceId}/teams/${teamAccessId}`).pipe(
      tap(() => {
        this.documentSpaceStore.update(documentSpaceId, documentSpace => {
          return {
            teams: documentSpace.teams.filter(c => c.id !== teamAccessId)
          }
        })
      })
    )
  }

  /**
   * Fetches the space documents
   * @param {string} documentSpaceId
   * @returns {Observable<Document[]>}
   */
  fetchSpaceDocuments(documentSpaceId: string): Observable<Document[]> {
    return this.apiClient.getAll<Document[]>(`${this.documentSpaceUrl}/${documentSpaceId}/documents`).pipe(
      map(documents => {
        return documents.map(document => new Document(document))
      }),
      tap(documents => {
        const storedDocuments = this.documentQuery.querySpaceDocuments(documentSpaceId)
        this.documentStore.sync(documents, this.getStoredDocumentsForListSync(storedDocuments()))
      })
    )
  }

  /**
   * Gets stored list documents for a list sync without the active document.
   * This prevents a stale list response from temporarily removing the currently viewed document
   * while it is being moved into the list optimistically.
   * @param {Document[]} storedDocuments
   * @returns {Document[]}
   */
  private getStoredDocumentsForListSync(storedDocuments: Document[]): Document[] {
    const activeDocument = this.documentQuery.getActive()

    if (!activeDocument) {
      return storedDocuments
    }

    return storedDocuments.filter(document => document.id !== activeDocument.id)
  }

  /**
   * Fetches the project documents
   * @param {string} projectId
   * @returns {Observable<Document[]>}
   */
  fetchProjectDocuments(projectId: string): Observable<Document[]> {
    return this.apiClient.getAll<Document[]>(`${apiEndpoint}/projects/${projectId}/documents`).pipe(
      map(documents => {
        return documents.map(document => new Document(document))
      }),
      tap(documents => {
        const storedDocuments = this.documentQuery.queryProjectDocuments(projectId)
        this.documentStore.sync(documents, storedDocuments())
      })
    )
  }

  /**
   * Fetches the private documents
   * @returns {Observable<Document[]>}
   */
  fetchPrivateDocuments(): Observable<Document[]> {
    return this.apiClient.getAll<Document[]>(`${apiEndpoint}/me/privatedocuments`).pipe(
      map(documents => {
        return documents.map(document => new Document(document))
      }),
      tap(documents => {
        const storedDocuments = this.documentQuery.queryPrivateDocuments()
        this.documentStore.sync(documents, storedDocuments())
      })
    )
  }

  /**
   * Fetches the shared documents
   * @returns {Observable<Document[]>}
   */
  fetchSharedDocuments(): Observable<Document[]> {
    return this.apiClient.getAll<Document[]>(`${apiEndpoint}/me/shareddocuments`).pipe(
      map(documents => {
        return documents.map(document => new Document(document))
      }),
      tap(documents => {
        const storedDocuments = this.documentQuery.querySharedDocuments()
        this.documentStore.sync(documents, storedDocuments())
      })
    )
  }

  /**
   * Fetches the favorite documents
   */

  /**
   * Fetches the deleted space documents
   * @param {string} documentSpaceId
   * @returns {Observable<Document[]>}
   */
  fetchDeletedSpaceDocuments(documentSpaceId: string): Observable<Document[]> {
    return this.apiClient.getAll<Document[]>(`${this.documentSpaceUrl}/${documentSpaceId}/documents/trash`).pipe(
      map(documents => {
        return documents.map(document => new Document(document))
      }),
      tap(documents => {
        const storedDocuments = this.documentQuery.queryDeletedSpaceDocuments(documentSpaceId)

        this.documentStore.sync(documents, storedDocuments())

        const documentIds = documents.map(document => document.id)

        this.removeDeletedDocumentsFromUserSettings(documentIds)
      })
    )
  }

  /**
   * Fetches the deleted project documents
   * @param {string} projectId
   * @returns {Observable<Document[]>}
   */
  fetchDeletedProjectDocuments(projectId: string): Observable<Document[]> {
    return this.apiClient.getAll<Document[]>(`${apiEndpoint}/projects/${projectId}/documents/trash`).pipe(
      map(documents => {
        return documents.map(document => new Document(document))
      }),
      tap(documents => {
        const storedDocuments = this.documentQuery.queryDeletedProjectDocuments(projectId)

        this.documentStore.sync(documents, storedDocuments())

        const documentIds = documents.map(document => document.id)

        this.removeDeletedDocumentsFromUserSettings(documentIds)
      })
    )
  }

  /**
   * Fetches the deleted private documents
   * @returns {Observable<Document[]>}
   */
  fetchDeletedPrivateDocuments(): Observable<Document[]> {
    return this.apiClient.getAll<Document[]>(`${apiEndpoint}/me/privatedocuments/trash`).pipe(
      map(documents => {
        return documents.map(document => new Document(document))
      }),
      tap(documents => {
        const storedDocuments = this.documentQuery.queryDeletedPrivateDocuments()

        this.documentStore.sync(documents, storedDocuments())

        const documentIds = documents.map(document => document.id)

        this.removeDeletedDocumentsFromUserSettings(documentIds)
      })
    )
  }

  /**
   * Duplicates a document
   * @param {string} documentId
   * @returns {Observable<void>}
   */
  duplicateDocument(documentId: string): Observable<void> {
    return this.apiClient.post<void>(`${this.documentUrl}/${documentId}/duplicate`, {})
  }

  /**
   * Sets the active document
   * @param {string} documentId
   */
  setActiveDocument(documentId: string): void {
    this.documentStore.setActive(documentId)
  }

  /**
   * Sets the active document space
   * @param {string} documentSpaceId
   */
  setActiveDocumentSpace(documentSpaceId: string): void {
    this.documentSpaceStore.setActive(documentSpaceId)
  }

  /**
   * Saves the user settings related to documents
   * @param {Partial<IDocumentSettings>} settings
   */
  saveDocumentUserSettings(settings: Partial<IDocumentSettings>): void {
    this.userSettingsService.saveUserSettings('documents', settings)
  }

  /**
   * Adds a document to the recentDocuments user setting, ensuring settings are fetched first
   * @param {string} documentId The document id to add/move to top
   */
  addRecentDocument(documentId: string): void {
    // Read the latest server-side 'documents' user setting WITHOUT touching the store,
    // merge only the recentDocuments list, and then save just that field to avoid clobbering expanded state.
    this.userSettingsService.getUserSetting('documents').subscribe({
      next: response => {
        const serverRecents = this.extractRecentDocumentsFromServerValue(response?.body?.value)
        const mergedRecents = this.mergeDocumentIntoRecentList(documentId, serverRecents)

        this.saveRecentDocuments(mergedRecents)
      },
      error: () => {
        // Fallback to local merge if server read fails
        const localRecents = this.settingsQuery.getValue().documents?.recentDocuments || []
        const mergedRecents = this.mergeDocumentIntoRecentList(documentId, localRecents)

        this.saveRecentDocuments(mergedRecents)
      }
    })
  }

  /**
   * Parses the raw server 'documents' user setting value and extracts normalized recent documents.
   * @param {string | undefined} rawValue The raw JSON string stored on the server
   * @returns {{ id: string; lastOpened: Date; }[]} Normalized recent documents
   */
  private extractRecentDocumentsFromServerValue(rawValue: string | undefined): { id: string; lastOpened: Date }[] {
    if (!rawValue) {
      return []
    }

    try {
      const parsed = JSON.parse(rawValue) as { recentDocuments?: { id: string; lastOpened: string | Date }[] }

      if (!Array.isArray(parsed?.recentDocuments)) {
        return []
      }

      return parsed.recentDocuments.map(doc => {
        return { id: doc.id, lastOpened: new Date(doc.lastOpened) }
      })
    } catch {
      return []
    }
  }

  /**
   * Merges the given document id at the top of the recent list, removes duplicates,
   * sorts by lastOpened desc, and trims to MAX_RECENT_DOCUMENTS.
   * @param {string} documentId The document id to upsert in the list
   * @param {{ id: string; lastOpened: Date; }[]} existingRecents The existing recent documents
   * @returns {{ id: string; lastOpened: Date; }[]} The merged recent documents
   */
  private mergeDocumentIntoRecentList(
    documentId: string,
    existingRecents: { id: string; lastOpened: Date }[]
  ): { id: string; lastOpened: Date }[] {
    const now = new Date()
    const filtered = (existingRecents || []).filter(recent => recent.id !== documentId)
    const merged = [{ id: documentId, lastOpened: now }, ...filtered]
      .sort((a, b) => new Date(b.lastOpened).getTime() - new Date(a.lastOpened).getTime())
      .slice(0, MAX_RECENT_DOCUMENTS)

    return merged
  }

  /**
   * Persists the recent documents subset to user settings.
   * @param {{ id: string; lastOpened: Date; }[]} recentDocuments
   */
  private saveRecentDocuments(recentDocuments: { id: string; lastOpened: Date }[]): void {
    this.saveDocumentUserSettings({ recentDocuments })
  }

  /**
   * Creates the form date of a document to create or update it
   * @param {Document} document
   * @returns {FormData}
   */
  private createDocumentFormData(document: Document): FormData {
    const formData = new FormData()

    formData.append('name', document.name ?? '')
    formData.append('emoji', document.emoji ?? '')
    formData.append('isPrivate', document.isPrivate ? 'true' : 'false')

    if (document.content !== undefined) {
      const file = new File([document.content ?? ''], `${document.id}.txt`, { type: 'text/plain' })

      formData.append('content', file, file.name)
    }

    if (document.projectId !== undefined) {
      formData.append('projectId', document.projectId)
    }

    if (document.documentSpaceId !== undefined) {
      formData.append('documentSpaceId', document.documentSpaceId)
    }

    if (document.parentId !== undefined) {
      formData.append('parentId', document.parentId)
    }

    return formData
  }

  /**
   * Removes deleted documents from the user's favorite and recent document
   * @param {string[]} documentIds
   */
  private removeDeletedDocumentsFromUserSettings(documentIds: string[]): void {
    // Remove the deleted documents from the favorite documents
    if (this.documentSettings()?.favoriteDocuments?.some(id => documentIds.includes(id))) {
      const favoriteDocuments = this.documentSettings().favoriteDocuments.filter(id => !documentIds.includes(id))

      this.saveDocumentUserSettings({ favoriteDocuments })
    }

    // Remove the deleted documents from the recent documents
    if (this.documentSettings()?.recentDocuments?.some(doc => documentIds.includes(doc.id))) {
      const recentDocuments = this.documentSettings().recentDocuments.filter(doc => !documentIds.includes(doc.id))

      this.saveDocumentUserSettings({ recentDocuments })
    }
  }

  /**
   * Imports documents
   */
  importDocuments(): void {
    const subdomain = { name: 'notion-importer', isDefault: false }
    this.accountService.switchWorkspace(this.currentWorkspace().id, subdomain).subscribe({
      next: () => {
        window.open(`https://notion-importer.awork.com`, '_blank')
      },
      error: error => {
        console.error('Failed to get cookie for notion-importer subdomain', error)
      }
    })
  }

  /**
   * Notifies another user about a document mention; self-mentions complete without a request.
   * @param {string} documentId
   * @param {string} userId
   * @returns {Observable<void>}
   */
  sendDocumentMention(documentId: string, userId: string): Observable<void> {
    if (userId === this.userQuery.getCurrentUser().id) {
      return of(void 0)
    }

    return this.apiClient.post<void>(`${this.documentUrl}/${documentId}/mentions`, { userId })
  }
}
